import {createTheme, alpha, PaletteMode, Shadows} from '@mui/material/styles';

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 35%)', // Исправлено для лучшего контраста
  700: 'hsl(210, 100%, 30%)', // Исправлено для лучшего контраста
  800: 'hsl(210, 100%, 20%)', // Исправлено для лучшего контраста
  900: 'hsl(210, 100%, 15%)', // Исправлено для лучшего контраста
};

export const gray = {
  // Светлая тема
  50: 'hsl(220, 20%, 99%)', // Почти белый
  100: 'hsl(220, 15%, 95%)', // Очень светлый серый
  200: 'hsl(223, 6.40%, 21.40%)', // Светлый серый
  300: 'hsl(220, 10%, 80%)', // Серый
  400: 'hsl(220, 10%, 65%)', // Средний серый
  500: 'hsl(220, 10%, 45%)', // Темный серый
  // Темная тема
  600: 'hsl(220, 10%, 35%)', // Светлый серый (темная тема)
  700: 'hsl(220, 15%, 25%)', // Средний серый (темная тема)
  800: 'hsl(220, 20%, 15%)', // Темный серый (темная тема)
  900: 'hsl(220, 25%, 10%)', // Очень темный серый (темная тема)
};

export const green = {
  50: 'hsl(120, 60%, 98%)', // Очень светлый зеленый
  100: 'hsl(120, 55%, 94%)', // Светлый зеленый
  200: 'hsl(120, 50%, 87%)', // Светло-зеленый
  300: 'hsl(120, 45%, 77%)', // Зеленый
  400: 'hsl(120, 50%, 53%)', // Яркий зеленый
  500: 'hsl(120, 65%, 30%)', // Темный зеленый
  600: 'hsl(120, 70%, 25%)', // Очень темный зеленый
  700: 'hsl(120, 75%, 20%)', // Глубокий зеленый (темная тема)
  800: 'hsl(120, 80%, 15%)', // Насыщенный зеленый (темная тема)
  900: 'hsl(120, 85%, 10%)', // Очень темный зеленый (темная тема)
};

export const orange = {
  50: 'hsl(30, 100%, 97%)', // Очень светлый оранжевый
  100: 'hsl(30, 95%, 90%)', // Светлый оранжевый
  200: 'hsl(30, 90%, 80%)', // Светло-оранжевый
  300: 'hsl(30, 85%, 65%)', // Оранжевый
  400: 'hsl(30, 80%, 50%)', // Яркий оранжевый
  500: 'hsl(30, 85%, 40%)', // Темный оранжевый
  600: 'hsl(30, 90%, 30%)', // Очень темный оранжевый
  700: 'hsl(30, 95%, 25%)', // Глубокий оранжевый (темная тема)
  800: 'hsl(30, 100%, 20%)', // Насыщенный оранжевый (темная тема)
  900: 'hsl(30, 100%, 15%)', // Очень темный оранжевый (темная тема)
};

export const red = {
  50: 'hsl(0, 100%, 98%)', // Очень светлый красный
  100: 'hsl(0, 95%, 94%)', // Светлый красный
  200: 'hsl(0, 90%, 88%)', // Светло-красный
  300: 'hsl(0, 85%, 77%)', // Красный
  400: 'hsl(0, 80%, 60%)', // Яркий красный
  500: 'hsl(0, 85%, 45%)', // Темный красный
  600: 'hsl(0, 90%, 35%)', // Очень темный красный
  700: 'hsl(0, 95%, 30%)', // Глубокий красный (темная тема)
  800: 'hsl(0, 100%, 25%)', // Насыщенный красный (темная тема)
  900: 'hsl(0, 100%, 20%)', // Очень темный красный (темная тема)
};

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px';

  return {
    palette: {
      mode,
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
        ...(mode === 'dark' && {
          contrastText: brand[50],
          light: brand[300],
          main: brand[400],
          dark: brand[700],
        }),
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
        ...(mode === 'dark' && {
          contrastText: brand[300],
          light: brand[500],
          main: brand[700],
          dark: brand[900],
        }),
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)',
        ...(mode === 'dark' && {
          default: gray[900],
          paper: 'hsl(220, 30%, 7%)',
        }),
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        ...(mode === 'dark' && {
          primary: 'hsl(0, 0%, 100%)',
          secondary: gray[400],
        }),
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        disabled: `red`,
        ...(mode === 'dark' && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3),
        }),
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        default: 'hsl(0, 0%, 99%)',
        paper: 'hsl(220, 35%, 97%)',
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        disabled: `${alpha(gray[200], 0.4)}`,
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[50],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      info: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: 'hsl(220, 30%, 7%)',
      },
      text: {
        primary: 'hsl(0, 0%, 100%)',
        secondary: gray[400],
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;
